import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AccountInfo } from '@azure/msal-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectChangeEvent } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { environment } from '../../../../../environment/environment';
import { IconRoute, routes } from '../../../app.routes';
import { AssetNavigationService } from '../../../services/asset-navigation.service';
import { AuthService, AuthServiceUserInfo } from '../../../services/auth.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { UserImpersonationService } from '../../../services/user-impersonation.service';
import { AccessRightDomainStore } from '../../../stores/access-right.store';
import { AssetGroupStore } from '../../../stores/asset-group.store';
import { AssetStore } from '../../../stores/asset.store';
import { UserStore } from '../../../stores/user.store';
import { Asset } from '../../../types/asset.interface';
import { AssetGroupSelectItem, groupAssetsByAssetGroup } from '../../../utils/asset-grouping.util';
import { RouteUtils } from '../../../utils/route.utils';
import { getInitials } from '../../../utils/strings';
import { CesButtonComponent, CesButtonTertiaryComponent, CesToolbarButtonComponent } from '../ces-buttons';
import { CesIconComponent } from '../ces-icon/ces-icon.component';
import { LogoComponent } from '../logo/logo.component';
import { SearchDropdownComponent } from '../search-dropdown/search-dropdown.component';
import { WebsocketService } from '../../../services/websocket.service';

const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

@Component({
  selector: 'ces-toolbar',
  standalone: true,
  imports: [
    AvatarModule,
    CommonModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    ProgressBarModule,
    RouterModule,
    SearchDropdownComponent,
    LogoComponent,
    CesIconComponent,
    ToggleSwitchModule,
    CesToolbarButtonComponent,
    CesButtonComponent,
    CesButtonTertiaryComponent,
    TranslocoModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative',
  },
})
export class HeaderComponent implements OnInit {
  protected readonly RouteUtils = RouteUtils;
  protected readonly authenticatedUser$: AuthServiceUserInfo;
  protected readonly getInitials = getInitials;

  protected isConfigActive: boolean = false;
  protected currentUrl: string = '';
  protected groupedAssets: AssetGroupSelectItem[] = [];
  protected isLoading: boolean = false;

  protected readonly menuItems = signal<MenuItem[]>([]);
  protected readonly displayedUserName = computed(() => {
    const impersonatedUser = this.impersonationService.impersonatedUser();
    if (impersonatedUser) {
      return impersonatedUser.displayName;
    }
    return this.currentUser?.name ?? 'N/A';
  });
  protected readonly displayedUserInitials = computed(() => getInitials(this.displayedUserName()));

  protected readonly currentLang = signal<SupportedLanguage>(this.getInitialLang());
  protected readonly currentLangLabel = computed(() => this.currentLang().toUpperCase());
  protected readonly languageMenuItems = signal<MenuItem[]>([]);

  private readonly assetGroupStore = inject(AssetGroupStore);
  private readonly assetStore = inject(AssetStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly accessRightStore = inject(AccessRightDomainStore);
  private readonly assetNavigationService = inject(AssetNavigationService);
  private readonly authorizationService = inject(AuthorizationService);
  protected readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  protected readonly websocket = inject(WebsocketService);

  protected readonly loadingService = inject(LoadingService);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  currentUser: AccountInfo | null = null;

  protected readonly isAdminOrDev = this.authorizationService.isAdminOrDev;
  protected readonly isDev = this.authorizationService.isDev;
  protected readonly isImpersonating = this.impersonationService.isImpersonating;
  protected readonly impersonatedUser = this.impersonationService.impersonatedUser;
  protected readonly showImpersonationDialog = signal(false);
  protected readonly selectedImpersonationUserId = signal<number | null>(null);

  protected readonly currentUserEntity = computed(() => {
    const userId = this.authService.currentUserId();
    if (!userId) return null;
    return this.userStore.byId()(userId) ?? null;
  });

  protected readonly canShowImpersonation = computed(() => {
    const user = this.currentUserEntity();
    return !this.isImpersonating() && user !== null && this.impersonationService.canImpersonate(user);
  });

  protected readonly availableUsers = this.impersonationService.availableUsers;

  @ViewChild(SearchDropdownComponent) searchDropdownComponent!: SearchDropdownComponent;
  @ViewChild('userMenuButton') userMenuButton!: CesToolbarButtonComponent;
  @ViewChild('langMenuButton') langMenuButton!: CesToolbarButtonComponent;

  constructor() {
    this.authenticatedUser$ = this.authService.authenticatedUser$;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
        this.updateActiveState();
      });
  }

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.updateActiveState();

    this.translateService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.languageMenuItems.set(this.buildLanguageMenu());
        this.menuItems.set(this.buildUserMenu());
      });

    this.authService.authenticatedUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.currentUser = user;
      if (user) {
        void this.loadAssetsOnceUserKnown();
        const translation = this.translateService.getTranslation();
        if (translation && Object.keys(translation).length > 0) {
          this.menuItems.set(this.buildUserMenu());
        }
      }
    });
  }

  private async loadAssetsOnceUserKnown(): Promise<void> {
    await this.authService.waitForUsers();
    if (this.authService.currentUserId()) {
      await this.loadAssets();
      return;
    }
    // Without a CESmon user the asset list stays empty: say why instead.
    this.toastService.error(this.userStore.entities().length > 0 ? 'common.toasts.userNotAuthenticated' : 'common.toasts.loadFailed');
  }

  private buildUserMenu(): MenuItem[] {
    const menuItems: MenuItem[] = [];
    let label = 'Menu';

    const accountRoute: IconRoute | undefined = routes.find(route => route.path === 'account');
    if (accountRoute?.children) {
      const titleKey = (accountRoute.children as IconRoute[]).filter(route => !route.path && !route.outlet)?.at(0)?.title as string;
      label = titleKey ? this.translateService.translate(titleKey) : label;

      const devOnlyRoutes = ['pocs', 'component-list'];
      const excludedRoutes = ['logout', 'settings'];
      menuItems.push(
        ...(accountRoute.children as IconRoute[])
          .filter(route => route.path && route.title && !route.outlet)
          .filter(route => !excludedRoutes.includes(route.path as string))
          .filter(route => !devOnlyRoutes.includes(route.path as string) || this.isDev())
          .map(route => ({
            label: this.translateService.translate(route.title as string),
            icon: route.icon as string,
            routerLink: `/account/${route.path}`,
          })),
      );
    }

    const conditionalItems: Array<{ order: number; item: MenuItem; show: boolean }> = [
      {
        order: 1,
        item: {
          label: this.translateService.translate('navigation.testRights'),
          icon: 'people',
          command: () => this.openImpersonationDialog(),
        },
        show: this.canShowImpersonation(),
      },
      {
        order: 3,
        item: {
          label: this.translateService.translate('navigation.testsPermissions'),
          icon: 'bug_report',
          routerLink: '/test-permission',
        },
        show: this.isDev(),
      },
      {
        order: 100,
        item: {
          label: this.translateService.translate('navigation.logout'),
          icon: 'logout',
          routerLink: '/account/logout',
        },
        show: true,
      },
    ];

    conditionalItems
      .filter(config => config.show)
      .sort((a, b) => a.order - b.order)
      .forEach(config => menuItems.splice(config.order - 1, 0, config.item));

    return [{ label, items: menuItems }];
  }

  protected onAssetSelected(event: SelectChangeEvent): void {
    if (event.value && typeof event.value === 'number') {
      void this.assetNavigationService.navigateToAsset(event.value);
      setTimeout(() => {
        if (this.searchDropdownComponent) {
          this.searchDropdownComponent.reset();
        }
      }, 300);
    }
  }

  protected onSearchCleared(): void {
    this.prepareAllAssets();
  }

  private async loadAssets(): Promise<void> {
    this.isLoading = true;
    try {
      const realUserId = this.authService.currentUserId();
      const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

      if (effectiveUserId) {
        await Promise.all([
          this.assetGroupStore.loadAll(),
          this.assetStore.loadAll(),
          this.accessRightStore.loadByUserId(effectiveUserId),
        ]);
      } else {
        await Promise.all([this.assetGroupStore.loadAll(), this.assetStore.loadAll()]);
      }

      this.prepareAllAssets();
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      this.isLoading = false;
    }
  }

  protected searchAssets(term: string): void {
    if (!term || term.trim().length === 0) {
      this.prepareAllAssets();
      return;
    }

    const normalizedTerm = term.toLowerCase().trim();
    const matchingAssets: Asset[] = this.assetStore.search(normalizedTerm);
    const allowedAssets = this.filterAssetsByPermissions(matchingAssets);
    this.groupedAssets = groupAssetsByAssetGroup(allowedAssets, this.assetGroupStore.entities());
  }

  private prepareAllAssets(): void {
    const allowedAssets = this.filterAssetsByPermissions(this.assetStore.entities());
    this.groupedAssets = groupAssetsByAssetGroup(allowedAssets, this.assetGroupStore.entities());
  }

  private filterAssetsByPermissions(assets: Asset[]): Asset[] {
    if (environment.name === 'dev') {
      return assets;
    }

    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

    if (!effectiveUserId) {
      return assets;
    }

    if (!this.isImpersonating()) {
      const user = this.userStore.byId()(effectiveUserId);
      if (user?.isAdmin) {
        return assets;
      }
    }

    const userAccessRights = this.accessRightStore.entities().filter(ar => ar.userId === effectiveUserId);
    const allowedAssetIds = new Set(userAccessRights.map(ar => ar.assetId));

    return assets.filter(asset => allowedAssetIds.has(asset.id));
  }

  private updateActiveState(): void {
    this.isConfigActive = this.currentUrl.startsWith('/configuration');
  }

  stopImpersonation(): void {
    this.impersonationService.stopImpersonation();
    this.impersonationService.reloadAfterImpersonationChange();
  }

  openImpersonationDialog(): void {
    this.showImpersonationDialog.set(true);
    this.selectedImpersonationUserId.set(null);
  }

  closeImpersonationDialog(): void {
    this.showImpersonationDialog.set(false);
    this.selectedImpersonationUserId.set(null);
  }

  startImpersonation(): void {
    const userId = this.selectedImpersonationUserId();
    if (!userId) return;

    const success = this.impersonationService.startImpersonation(userId);
    if (success) {
      this.closeImpersonationDialog();
      this.impersonationService.reloadAfterImpersonationChange();
    }
  }

  private getInitialLang(): SupportedLanguage {
    const stored = localStorage.getItem('lang');
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
    return 'fr';
  }

  private buildLanguageMenu(): MenuItem[] {
    return SUPPORTED_LANGUAGES.map(lang => ({
      label: lang.toUpperCase(),
      command: () => this.onLanguageChange(lang),
    }));
  }

  protected onLanguageChange(lang: SupportedLanguage): void {
    localStorage.setItem('lang', lang);
    this.translateService.setActiveLang(lang);
    this.currentLang.set(lang);
  }

  protected onUserMenuShow(): void {
    this.langMenuButton?.menu?.hide();
  }

  protected onLangMenuShow(): void {
    this.userMenuButton?.menu?.hide();
  }
}
